import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Clock3, Copy, Eye, FileSignature, Link2, ShieldCheck, Trash2, UserRound } from "lucide-react";
import { LocalizedScope, useWorkspaceTranslation } from "@/locales/workspaceExtras";

const EVENT_LABELS = {
  created: "Link created", viewed: "Document viewed", downloaded: "Document downloaded",
  signed: "Document signed", revoked: "Access revoked",
};

const DATE_LOCALES = { en: "en-GB", it: "it-IT", de: "de-DE", es: "es-ES", zh: "zh-CN", ru: "ru-RU" };
function dateTime(value, lang) {
  if (!value) return "—";
  return new Intl.DateTimeFormat(DATE_LOCALES[lang] || DATE_LOCALES.en, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export default function ExternalSharingPanel({ deal, drafts }) {
  const { lang, tr } = useWorkspaceTranslation();
  const [shares, setShares] = useState([]);
  const [activities, setActivities] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);
  const [freshLink, setFreshLink] = useState("");
  const [form, setForm] = useState({
    draft_id: "", recipient_name: "", recipient_email: "", expires_in_days: 7,
    allow_download: true, allow_sign: false,
  });

  const finalDrafts = useMemo(() => drafts.filter((item) => item.workflow_status === "final"), [drafts]);

  const load = useCallback(async () => {
    try { setShares(await api.listExternalShares(deal.id)); }
    catch { toast.error(tr("Could not load external access links")); }
  }, [deal.id, tr]);

  useEffect(() => { load(); }, [load]);

  const create = async (event) => {
    event.preventDefault();
    if (!form.draft_id) return toast.error(tr("Select a document"));
    setCreating(true);
    try {
      const permissions = ["view"];
      if (form.allow_download) permissions.push("download");
      if (form.allow_sign) permissions.push("sign");
      const item = await api.createExternalShare({
        deal_id: deal.id, draft_id: form.draft_id,
        recipient_name: form.recipient_name || null, recipient_email: form.recipient_email || null,
        expires_in_days: Number(form.expires_in_days), permissions,
      });
      const link = `${window.location.origin}/share/${item.access_token}`;
      setFreshLink(link);
      setForm((current) => ({ ...current, recipient_name: "", recipient_email: "" }));
      await load();
      toast.success(tr("Protected recipient link created"));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(tr(detail?.code === "document_not_final" ? "Only a final document can be signed" : "Could not create the link"));
    } finally { setCreating(false); }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(freshLink);
    toast.success(tr("Link copied"));
  };

  const revoke = async (id) => {
    if (!window.confirm(tr("Revoke this recipient's access?"))) return;
    try { await api.revokeExternalShare(id); await load(); toast.success(tr("Access revoked")); }
    catch { toast.error(tr("Could not revoke access")); }
  };

  const toggleActivity = async (id) => {
    if (expanded === id) return setExpanded(null);
    setExpanded(id);
    if (!activities[id]) {
      try {
        const events = await api.listExternalShareActivity(id);
        setActivities((current) => ({ ...current, [id]: events }));
      }
      catch { toast.error(tr("Could not load recipient activity")); }
    }
  };

  return (
    <LocalizedScope><div className="space-y-6" data-testid="external-sharing-panel">
      <div className="grid xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,.85fr)] gap-6">
        <div className="card-surface p-6">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-9 h-9 border border-[var(--td-gold-muted)] flex items-center justify-center text-[var(--td-gold)]"><Link2 size={17} /></div>
            <div>
              <h2 className="font-serif text-xl">Create protected access</h2>
              <p className="text-xs text-[var(--td-text-3)] mt-1">Share a frozen document version with expiry and precise permissions.</p>
            </div>
          </div>
          <form onSubmit={create} className="space-y-4" data-testid="share-create-form">
            <div>
              <label className="td-label">Document</label>
              <select className="td-input" value={form.draft_id} data-testid="share-draft"
                      onChange={(e) => setForm({ ...form, draft_id: e.target.value })} required>
                <option value="">Select document…</option>
                {(form.allow_sign ? finalDrafts : drafts).map((item) => <option key={item.id} value={item.id}>{item.title || item.template_code} · v{item.version_number} · {item.workflow_status}</option>)}
              </select>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="td-label">Recipient name</label><input className="td-input" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} /></div>
              <div><label className="td-label">Recipient email</label><input type="email" className="td-input" value={form.recipient_email} data-testid="share-recipient-email" onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} /></div>
            </div>
            <div>
              <label className="td-label">Access period</label>
              <select className="td-input" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}>
                <option value="1">1 day</option><option value="3">3 days</option><option value="7">7 days</option>
                <option value="14">14 days</option><option value="30">30 days</option><option value="90">90 days</option>
              </select>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-3 border border-[var(--td-border)] p-3 cursor-pointer">
                <input type="checkbox" checked={form.allow_download} onChange={(e) => setForm({ ...form, allow_download: e.target.checked })} />
                <span className="text-sm">Allow PDF / Word download</span>
              </label>
              <label className="flex items-center gap-3 border border-[var(--td-border)] p-3 cursor-pointer">
                <input type="checkbox" checked={form.allow_sign} data-testid="share-allow-sign"
                       onChange={(e) => {
                         const enabled = e.target.checked;
                         const selectedIsFinal = finalDrafts.some((item) => item.id === form.draft_id);
                         setForm({ ...form, allow_sign: enabled, draft_id: enabled && !selectedIsFinal ? (finalDrafts[0]?.id || "") : form.draft_id });
                       }} />
                <span className="text-sm">Request electronic signature</span>
              </label>
            </div>
            {form.allow_sign && finalDrafts.length === 0 && <p className="text-xs text-amber-300">Mark a document as Final before requesting a signature.</p>}
            <button className="btn-primary inline-flex items-center gap-2" disabled={creating} data-testid="share-create">
              <ShieldCheck size={15} /> {creating ? "Creating…" : "Create protected link"}
            </button>
          </form>
        </div>

        <div className="card-surface p-6">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[var(--td-gold)] mb-4">Security model</div>
          <div className="space-y-4 text-sm text-[var(--td-text-2)]">
            <Feature icon={ShieldCheck} title="Unrecoverable access key" text="Only the recipient URL contains the key. TradeDocs stores its cryptographic fingerprint." />
            <Feature icon={Clock3} title="Automatic expiry" text="The link stops working when its access period ends, or immediately after revocation." />
            <Feature icon={Eye} title="Recipient activity" text="Views, downloads, signing and revocation are recorded in one evidence trail." />
            <Feature icon={FileSignature} title="Frozen signed version" text="The shared content and its document hash remain fixed even if the working draft changes." />
          </div>
        </div>
      </div>

      {freshLink && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-5" data-testid="share-fresh-link">
          <div className="flex items-center gap-2 text-emerald-300 text-sm mb-2"><Check size={15} /> Protected link ready — copy it now</div>
          <div className="flex gap-2"><input readOnly value={freshLink} className="td-input font-mono text-xs" /><button onClick={copy} className="btn-ghost shrink-0"><Copy size={14} /></button></div>
          <p className="text-[11px] text-[var(--td-text-3)] mt-2">For security, the complete URL is shown only once.</p>
        </div>
      )}

      <div className="card-surface overflow-hidden">
        <div className="p-5 border-b border-[var(--td-border)]"><h2 className="font-serif text-xl">Recipient access</h2></div>
        {shares.length === 0 ? <div className="p-8 text-sm text-[var(--td-text-3)]">No external access links for this deal.</div> : (
          <div className="divide-y divide-[var(--td-border)]">
            {shares.map((item) => (
              <div key={item.id} data-testid="share-row">
                <div className="p-5 flex flex-wrap items-center gap-4">
                  <div className="w-9 h-9 rounded-full border border-[var(--td-border)] flex items-center justify-center"><UserRound size={15} /></div>
                  <div className="min-w-[220px] flex-1">
                    <div className="text-sm">{item.recipient_name || item.recipient_email || "Unnamed recipient"}</div>
                    <div className="text-xs text-[var(--td-text-3)] mt-1">{item.document_title} · expires {dateTime(item.expires_at, lang)}</div>
                  </div>
                  <Status value={item.status} />
                  <button className="btn-ghost !py-2 text-xs" onClick={() => toggleActivity(item.id)}><Eye size={13} className="inline mr-2" />Activity</button>
                  {item.status === "active" && <button className="btn-ghost !py-2 !text-red-300" onClick={() => revoke(item.id)} data-testid="share-revoke"><Trash2 size={13} /></button>}
                </div>
                {expanded === item.id && (
                  <div className="px-5 pb-5 ml-12">
                    <div className="border-l border-[var(--td-gold-muted)] pl-4 space-y-3">
                      {(activities[item.id] || []).map((event) => <div key={event.id} className="text-xs"><span className="text-[var(--td-text)]">{EVENT_LABELS[event.event_type] || event.event_type}</span><span className="text-[var(--td-text-3)]"> · {dateTime(event.created_at, lang)}</span></div>)}
                      {!activities[item.id] && <div className="text-xs text-[var(--td-text-3)]">Loading…</div>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div></LocalizedScope>
  );
}

function Feature({ icon: Icon, title, text }) {
  return <div className="flex gap-3"><Icon size={16} className="text-[var(--td-gold)] mt-0.5 shrink-0" /><div><div className="text-[var(--td-text)]">{title}</div><div className="text-xs leading-relaxed mt-1">{text}</div></div></div>;
}

function Status({ value }) {
  const { tr } = useWorkspaceTranslation();
  const styles = { active: "text-sky-300 border-sky-500/30", signed: "text-emerald-300 border-emerald-500/30", expired: "text-amber-300 border-amber-500/30", revoked: "text-red-300 border-red-500/30" };
  return <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-1 ${styles[value] || "border-[var(--td-border)]"}`}>{tr(value)}</span>;
}
